// ./src/utils/api/ContactMessageService.ts
//
// The unified incoming-messages surface for an event. All messages (emails received at the event's
// inbound address AND website Contact Us submissions) live in the sender (microservices-sender)
// under `event/<id>`; eventsmagic-api proxies the reads/writes. This service exposes: the inbound
// status + address (inboundInfo), the on/off toggle (setInbound), the unified thread (inboundThread —
// email + form, tagged by `via`), and replying (replyInbound — sent FROM the event address, with it as
// Reply-To too, so the guest's reply loops back). The legacy event_contact_message CRUD/reply was retired
// when Contact Us moved onto the sender.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";

const INBOUND_INFO_QUERY = gql`
  query EventContactInboundInfo($eventId: ID) {
    eventContactInboundInfo(eventId: $eventId) {
      code
      errors { name errors warnings }
      data { eventId enabled address replyFromAddress }
    }
  }
`;

const SET_INBOUND_MUTATION = gql`
  mutation EventContactSetInbound($eventId: ID, $enabled: Boolean) {
    eventContactSetInbound(eventId: $eventId, enabled: $enabled) {
      code
      errors { name errors warnings }
      data { eventId enabled address replyFromAddress }
    }
  }
`;

const INBOUND_THREAD_QUERY = gql`
  query EventContactInboundThread($eventId: ID) {
    eventContactInboundThread(eventId: $eventId) {
      code
      errors { name errors warnings }
      data {
        id
        direction
        via
        counterpart
        counterpartName
        fromName
        fromEmail
        toEmail
        subject
        body
        bodyHtml
        deliveryStatus
        deliveryDetail
        attachments { uploadedFileId filename mimeType sizeBytes kind fileUrl thumbnailUrl isImage }
        providerMessageId
        createdAt
      }
    }
  }
`;

const THREAD_STATES_QUERY = gql`
  query EventContactThreadStates($eventId: ID) {
    eventContactThreadStates(eventId: $eventId) {
      code
      errors { name errors warnings }
      data { counterpart lastReadAt archivedAt }
    }
  }
`;

const MARK_READ_MUTATION = gql`
  mutation EventContactThreadMarkRead($eventId: ID, $counterpart: String) {
    eventContactThreadMarkRead(eventId: $eventId, counterpart: $counterpart) {
      code
      errors { name errors warnings }
      data { counterpart lastReadAt archivedAt }
    }
  }
`;

const SET_ARCHIVED_MUTATION = gql`
  mutation EventContactThreadSetArchived($eventId: ID, $counterpart: String, $archived: Boolean) {
    eventContactThreadSetArchived(eventId: $eventId, counterpart: $counterpart, archived: $archived) {
      code
      errors { name errors warnings }
      data { counterpart lastReadAt archivedAt }
    }
  }
`;

const REPLY_INBOUND_MUTATION = gql`
  mutation EventContactReplyInbound(
    $eventId: ID
    $toEmail: String
    $toName: String
    $body: String
    $bodyHtml: String
    $subject: String
    $originalBody: String
    $inReplyToId: ID
    $attachments: [EventContactAttachmentInput]
  ) {
    eventContactReplyInbound(
      eventId: $eventId
      toEmail: $toEmail
      toName: $toName
      body: $body
      bodyHtml: $bodyHtml
      subject: $subject
      originalBody: $originalBody
      inReplyToId: $inReplyToId
      attachments: $attachments
    ) {
      code
      errors { name errors warnings }
      data { eventId enabled address }
    }
  }
`;

export interface ContactInboundInfo {
  eventId?: string | null;
  enabled?: boolean | null;
  address?: string | null;
  /** The address a reply is sent FROM (A2e #83) — the event's own; null = the brand's no-reply sender. */
  replyFromAddress?: string | null;
}

export interface ContactAttachment {
  uploadedFileId?: string | null;
  filename?: string | null;
  mimeType?: string | null;
  sizeBytes?: number | null;
  // "image" | "video" | "audio" | "document" | "other".
  kind?: string | null;
  // Direct storage/CDN URL to fetch or preview the file.
  fileUrl?: string | null;
  thumbnailUrl?: string | null;
  // UI hint: render inline (image) vs a download chip.
  isImage?: boolean | null;
}

export interface ContactInboundMessage {
  id?: string;
  // "inbound" (from a guest) | "outbound" (a crew reply).
  direction?: string | null;
  // How it arrived: "email" | "form" | "reply".
  via?: string | null;
  // The guest this message's conversation is with (sender of inbound / recipient of a reply) — the grouping key.
  counterpart?: string | null;
  counterpartName?: string | null;
  fromName?: string | null;
  fromEmail?: string | null;
  toEmail?: string | null;
  subject?: string | null;
  body?: string | null;
  // Rich HTML body for an outbound crew reply (render sanitized); null for inbound.
  bodyHtml?: string | null;
  // Send status of an outbound reply: "queued" | "sent" | "delivered" | "failed" (null for inbound).
  deliveryStatus?: string | null;
  // Short human explanation when deliveryStatus is "failed".
  deliveryDetail?: string | null;
  attachments?: ContactAttachment[] | null;
  providerMessageId?: string | null;
  createdAt?: string | null;
}

export interface ContactThreadState {
  counterpart?: string | null;
  lastReadAt?: string | null;
  archivedAt?: string | null;
}

export class ContactMessageService {
  /** The event's inbound-email status (enabled + the parse address guests can write to). */
  static async inboundInfo(eventId: string): Promise<OpResult> {
    return ApiService.executeQuery(INBOUND_INFO_QUERY, { eventId }, { fetchPolicy: "network-only" });
  }

  /** Turn the event's inbound-email inbox on/off (server guarantees a slug when enabling). */
  static async setInbound(eventId: string, enabled: boolean): Promise<OpResult> {
    return ApiService.executeMutation(SET_INBOUND_MUTATION, { eventId, enabled });
  }

  /** The event's full contact conversation (inbound email/form + crew replies), for grouping into threads. */
  static async inboundThread(eventId: string): Promise<OpResult> {
    return ApiService.executeQuery(INBOUND_THREAD_QUERY, { eventId }, { fetchPolicy: "network-only" });
  }

  /** Per-conversation read/archived state (keyed by counterpart email) for the inbox badges. */
  static async threadStates(eventId: string): Promise<OpResult> {
    return ApiService.executeQuery(THREAD_STATES_QUERY, { eventId }, { fetchPolicy: "network-only" });
  }

  /** Mark a conversation read (clears its unread badge). */
  static async markThreadRead(eventId: string, counterpart: string): Promise<OpResult> {
    return ApiService.executeMutation(MARK_READ_MUTATION, { eventId, counterpart });
  }

  /** Archive / unarchive a conversation. */
  static async setThreadArchived(eventId: string, counterpart: string, archived: boolean): Promise<OpResult> {
    return ApiService.executeMutation(SET_ARCHIVED_MUTATION, { eventId, counterpart, archived });
  }

  /** Reply to a received message — sent FROM the event address when it has one (`replyFromAddress`). */
  static async replyInbound(args: {
    eventId: string;
    toEmail: string;
    toName?: string | null;
    body: string;
    bodyHtml?: string | null;
    subject?: string | null;
    originalBody?: string | null;
    inReplyToId?: string | null;
    attachments?: ContactAttachment[] | null;
  }): Promise<OpResult> {
    // Only the fields the reply-attachment input accepts (the sender's delivery worker needs fileUrl).
    // ms_storage returns sizeBytes as a pg-numeric STRING (e.g. "1150273.0000") — coerce to a real
    // number so the GraphQL Float doesn't reject it.
    const toNumber = (v: unknown): number | null => {
      const n = typeof v === "number" ? v : parseFloat(String(v ?? ""));

      return Number.isFinite(n) ? n : null;
    };

    const attachments = (args.attachments ?? [])
      .filter((a) => !!a.fileUrl)
      .map((a) => ({
        uploadedFileId: a.uploadedFileId ?? null,
        filename: a.filename ?? null,
        mimeType: a.mimeType ?? null,
        sizeBytes: toNumber(a.sizeBytes),
        kind: a.kind ?? null,
        fileUrl: a.fileUrl ?? null,
        thumbnailUrl: a.thumbnailUrl ?? null,
      }));

    return ApiService.executeMutation(REPLY_INBOUND_MUTATION, {
      eventId: args.eventId,
      toEmail: args.toEmail,
      toName: args.toName ?? null,
      body: args.body,
      bodyHtml: args.bodyHtml ?? null,
      subject: args.subject ?? null,
      originalBody: args.originalBody ?? null,
      inReplyToId: args.inReplyToId ?? null,
      attachments,
    });
  }
}
